import { useTranslations } from '@lang';
import { some } from '@utils/some';
import React, { useEffect, useRef, useState } from 'react';
import { MultiRoomSearch } from 'shared-types';
import { Button } from '../Button';
import { Divider } from '../Divider/Divider';
import { StyledButton } from '../Extras/Extra/layouts/shared/MoreDetailsButton/MoreDetailsButton.styles';
import { StyledPopper } from '../Popper';
import ChildAges, { ChildAgesProps } from './ChildAges';
import { GuestCounter } from './GuestCounter';
import {
  AddAnotherRoomButtonWrapper,
  EditingGuestsContainer,
  GuestPickerWrapper,
  RemoveText,
  RoomLabel,
  RoomLabelContainer,
  StyledTextInput,
} from './GuestPicker.styles';

type GuestPickerProps = {
  error?: string;
  required?: boolean;
  numberOfAdults: number;
  numberOfChildren: number;
  onGuestCountChange: (
    guestType: 'adults' | 'children',
    count: number,
    roomNumber?: number
  ) => void;
  onRoomCountChange: (roomNumber?: number) => void;
  isInsideModal?: boolean;
  disabledPlus?: boolean;
  disabledMinus?: boolean;
  isSingle: boolean;
  childAgesProps?: ChildAgesProps;
  onClose?: () => void;
  toggleEditGuestDetails?: (isEditing: boolean) => void;
  editingGuestDetails?: boolean;
  onFocus?: () => void;
  maxAdults?: number;
  maxChildren?: number;
  noLabel?: boolean;
  isMultiRoomsEnabled?: boolean;
  rooms?: MultiRoomSearch;
};

const MIN_ADULTS = 1;
const MIN_CHILDREN = 0;
const MAX_CHILDREN_DEFAULT = 4;

function GuestPicker({
  numberOfAdults: numAdults,
  numberOfChildren: numChildren = 0,
  isInsideModal = false,
  onGuestCountChange,
  error,
  required = false,
  disabledMinus = false,
  disabledPlus = false,
  isSingle,
  childAgesProps,
  onClose,
  toggleEditGuestDetails,
  editingGuestDetails,
  onFocus,
  maxAdults = 100,
  maxChildren = 100,
  noLabel,
  isMultiRoomsEnabled,
  rooms,
  onRoomCountChange,
}: GuestPickerProps) {
  const { t } = useTranslations();
  const [isGuestPopperOpen, setGuestPopperOpen] = useState(false);
  const popperRef = useRef<HTMLDivElement>(null);
  const requireChildAges = !isSingle && !!childAgesProps;
  const ageRangeText = requireChildAges
    ? t('guestsPicker.ageRange', {
        minChildAge: childAgesProps.minChildAge,
        maxChildAge: childAgesProps.maxChildAge,
      })
    : '';

  const roomsAdults = some(rooms) && rooms?.reduce((acc, room) => acc + room.adults, 0);
  const roomsChildren = some(rooms) && rooms?.reduce((acc, room) => acc + room.children, 0);
  const numberOfAdults = Math.max(MIN_ADULTS, roomsAdults || numAdults || MIN_ADULTS);
  const numberOfChildren = Math.max(MIN_CHILDREN, roomsChildren || numChildren || MIN_CHILDREN);

  const roomsWithLabels = (
    rooms || [{ roomNumber: 1, adults: numberOfAdults, children: numberOfChildren }]
  ).map((r, index) => ({ ...r, label: `${t('common.room')} ${index + 1}` }));

  function handleClickOutside(event) {
    const reactSelectOption = /^react-select-.+-option/;
    if (
      popperRef.current &&
      !popperRef.current.contains(event.target) &&
      !reactSelectOption.test(event.target.id)
    ) {
      if (onClose) {
        onClose();
      }
      setGuestPopperOpen(false);
    }
  }

  useEffect(() => {
    if (childAgesProps?.isSubmitted && childAgesProps?.errors.length) {
      setGuestPopperOpen(true);
      if (toggleEditGuestDetails) {
        toggleEditGuestDetails(true);
      }
    }
  }, [childAgesProps?.isSubmitted, childAgesProps?.errors.length]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, { capture: true });
    document.addEventListener('touchend', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside, { capture: true });
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <GuestPickerWrapper ref={popperRef} isInsideModal={isInsideModal} onFocus={onFocus}>
      {!editingGuestDetails && (
        <StyledTextInput
          isRequired={required}
          error={error}
          value={t(isSingle ? 'guestsPickerNoChildren.value' : 'guestsPicker.value', {
            numberOfAdults,
            numberOfChildren,
          })}
          onClick={() => {
            setGuestPopperOpen(true);
            if (toggleEditGuestDetails && requireChildAges) {
              toggleEditGuestDetails(!editingGuestDetails);
            }
          }}
          label={noLabel ? undefined : t('guestsPicker.label')}
          id="guest-picker"
        />
      )}
      {editingGuestDetails && toggleEditGuestDetails && requireChildAges && (
        <EditingGuestsContainer>
          <GuestCounter
            count={numberOfAdults}
            disabledMinus={disabledMinus || numberOfAdults <= MIN_ADULTS}
            disabledPlus={disabledPlus || numberOfAdults >= maxAdults}
            label={t('guestsPicker.adultsLabel')}
            onClickMinus={() => onGuestCountChange('adults', -1)}
            onClickPlus={() => onGuestCountChange('adults', 1)}
          />
          {!isSingle && (
            <>
              <GuestCounter
                count={numberOfChildren}
                disabledMinus={disabledMinus || numberOfChildren <= MIN_CHILDREN}
                disabledPlus={disabledPlus || numberOfChildren >= MAX_CHILDREN_DEFAULT}
                label={t('guestsPicker.childrenLabel')}
                labelDescription={ageRangeText}
                onClickMinus={() => onGuestCountChange('children', -1)}
                onClickPlus={() => onGuestCountChange('children', 1)}
              />
              <div>{requireChildAges && <ChildAges {...childAgesProps} />}</div>
            </>
          )}
        </EditingGuestsContainer>
      )}
      {isGuestPopperOpen && !editingGuestDetails && (
        <StyledPopper>
          {roomsWithLabels.map((room, index) => {
            const roomAdults = Math.max(MIN_ADULTS, room.adults || MIN_ADULTS);
            const roomChildren = Math.max(MIN_CHILDREN, room.children || MIN_CHILDREN);
            return (
              <React.Fragment key={room.roomNumber}>
                <RoomLabelContainer>
                  {isMultiRoomsEnabled && <RoomLabel>{room.label}</RoomLabel>}
                  {index !== 0 && (
                    <StyledButton onClick={() => onRoomCountChange(index)}>
                      <RemoveText>{t('common.remove')}</RemoveText>
                    </StyledButton>
                  )}
                </RoomLabelContainer>
                <GuestCounter
                  count={roomAdults}
                  disabledMinus={disabledMinus || roomAdults <= MIN_ADULTS}
                  disabledPlus={disabledPlus || roomAdults >= maxAdults}
                  label={t('guestsPicker.adultsLabel')}
                  onClickMinus={() => onGuestCountChange('adults', -1, room.roomNumber)}
                  onClickPlus={() => onGuestCountChange('adults', 1, room.roomNumber)}
                />
                {!isSingle && (
                  <>
                    <GuestCounter
                      count={roomChildren}
                      disabledMinus={disabledMinus || roomChildren <= MIN_CHILDREN}
                      disabledPlus={
                        disabledPlus ||
                        (requireChildAges
                          ? roomChildren >= MAX_CHILDREN_DEFAULT
                          : roomChildren >= maxChildren)
                      }
                      label={t('guestsPicker.childrenLabel')}
                      labelDescription={ageRangeText}
                      onClickMinus={() => onGuestCountChange('children', -1, room.roomNumber)}
                      onClickPlus={() => onGuestCountChange('children', 1, room.roomNumber)}
                    />
                    {requireChildAges && !isMultiRoomsEnabled && <ChildAges {...childAgesProps} />}
                  </>
                )}
                {isMultiRoomsEnabled && index !== roomsWithLabels.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
          {isMultiRoomsEnabled && (
            <AddAnotherRoomButtonWrapper>
              <Button isLarge={true} onClick={() => onRoomCountChange()}>
                {t('guestsPicker.addRoom')}
              </Button>
            </AddAnotherRoomButtonWrapper>
          )}
        </StyledPopper>
      )}
    </GuestPickerWrapper>
  );
}

export default GuestPicker;
